import { Component } from 'preact';
import {
  GOLD, TEXT_PRIMARY, TEXT_BODY, TEXT_MUTED, PANEL_BG, ACCENT_GOLD,
  BEVELED_BTN, GOLD_BTN, INPUT_STYLE,
  FourCorners,
} from '../../lib/medievalTheme';
import { UI } from '../../utils/arena/uiSounds';
import { CloseButton } from '../ui/navButtons';
import { canPlayDeck, buildCardCatalog } from '../../utils/sorcery/deckRules';
import { buildOwnedMap } from '../../utils/arena/collectionUtils';
import { pushDismiss, removeDismiss } from '../../utils/escStack';
import { t } from '../../localization/t';

/**
 * Deck spawn dialog for solo play. Styled like PileSearchDialog
 * (Cemetery / Spellbook / Atlas search). Shows the player's saved
 * decks as visual tiles and lets them spawn a deck as Player 1 or
 * Player 2 on the virtual tabletop.
 */
export default class DeckSpawnDialog extends Component {
  constructor(props) {
    super(props);
    this.state = {
      searchQuery: '',
    };
  }

  componentDidMount() {
    this._escDismissId = pushDismiss(() => this.props.onClose());
  }

  componentWillUnmount() {
    removeDismiss(this._escDismissId);
  }

  getFilteredDecks() {
    const collection = this.props.profile?.collection || [];
    const ownedMap = buildOwnedMap(collection);
    // Build the catalog once for this filter pass so avatar-structural
    // checks (Duplicator pairs etc.) don't pay an O(n) card-lookup
    // cost per deck. Solo play accepts any deck. Sandbox or
    // constructed-with-missing-cards. So ownership is skipped here.
    const cardCatalog = buildCardCatalog(this.props.sorceryCards);
    const decks = (this.props.savedDecks || []).filter((d) => canPlayDeck(d.cards || [], ownedMap, { cardCatalog, skipOwnership: true }).canPlay);
    const q = this.state.searchQuery.toLowerCase().trim();
    if (q.length < 2) return decks;
    return decks.filter((d) => d.name?.toLowerCase().includes(q));
  }

  render() {
    const { onClose, onSpawn, sorceryCards } = this.props;
    const { searchQuery } = this.state;
    const decks = this.getFilteredDecks();

    return (
      <div
        className="responsive-dialog-overlay"
        onClick={onClose}
      >
        <div
          className="responsive-dialog-panel"
          style={{
            background: `url("/tex-noise-panel.webp"), ${PANEL_BG}`,
            border: `1px solid ${GOLD} 0.22)`,
            borderRadius: 'var(--radius-xl)',
            boxShadow: `0 0 80px rgba(0,0,0,0.6), 0 0 30px ${GOLD} 0.04)`,
            width: '45cqw',
            height: '32.5cqw',
                        maxWidth: '92vw',
            maxHeight: '88vh',
            isolation: 'isolate',
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <FourCorners />

          {/* Header */}
          <div
            className="flex items-center gap-3 px-5 py-3.5 shrink-0"
            style={{ borderBottom: `1px solid ${GOLD} 0.12)` }}
          >
            <h2
              className="text-base font-bold arena-heading"
              style={{ color: TEXT_PRIMARY, textShadow: '0 2px 4px rgba(0,0,0,0.5)' }}
            >
              {t('Spawn Deck')}
            </h2>
            <span
              className="text-[0.6875cqw] px-2 py-0.5 rounded-md tabular-nums"
              style={{ background: `${GOLD} 0.08)`, color: TEXT_MUTED }}
            >
              {(this.props.savedDecks || []).length}
            </span>
            <div className="flex-1" />
            <input
              type="text"
              placeholder={t('Search decks...')}
              value={searchQuery}
              onInput={(e) => this.setState({ searchQuery: e.target.value })}
              className="px-3 py-1.5 text-xs"
              style={{ ...INPUT_STYLE, width: '12.5cqw' }}
            />
            <CloseButton onClick={onClose} />
          </div>

          {/* Deck grid */}
          <div className="flex-1 overflow-y-auto p-5 min-h-0">
            {decks.length === 0 ? (
              <div className="flex items-center justify-center h-full">
                <div className="text-center">
                  <div className="text-sm" style={{ color: TEXT_MUTED }}>
                    {searchQuery.length >= 2 ? t('No decks match your search') : t('No saved decks')}
                  </div>
                  <div className="text-xs mt-1" style={{ color: `${GOLD} 0.2)` }}>
                    {searchQuery.length >= 2 ? t('Try a different name') : t('Build and save a deck first')}
                  </div>
                </div>
              </div>
            ) : (
              <div
                className="grid gap-4"
                style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(11.875cqw, 1fr))' }}
              >
                {decks.map((deck) => (
                  <DeckTile
                    key={deck.id}
                    deck={deck}
                    sorceryCards={sorceryCards}
                    onSpawn={onSpawn}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }
}

function DeckTile({ deck, sorceryCards, onSpawn }) {
  // Find the avatar card for a preview image. Prefer the entry in the
  // primary slot. Imposter decks carry extras and the first Avatar by
  // array order is not necessarily the equipped face.
  const avatarEntry = deck.cards?.find((c) => {
    if (c.zone !== 'avatar') return false;
    const card = sorceryCards?.find((sc) => sc.unique_id === c.cardId);
    return card?.type === 'Avatar';
  }) || deck.cards?.find((c) => {
    const card = sorceryCards?.find((sc) => sc.unique_id === c.cardId);
    return card?.type === 'Avatar';
  });
  const avatarCard = avatarEntry
    ? sorceryCards?.find((sc) => sc.unique_id === avatarEntry.cardId)
    : null;
  const previewUrl = deck.previewUrl
    || avatarCard?.printings?.[0]?.image_url
    || null;

  return (
    <div
      className="group relative flex flex-col overflow-hidden transition-all duration-200 hover:scale-[1.02]"
      style={{
        background: '#0c0a08',
        border: `1px solid ${GOLD} 0.15)`,
        borderRadius: 'var(--radius-xl)',
      }}
    >
      {/* Preview image area */}
      <div className="relative w-full overflow-hidden" style={{ aspectRatio: '5 / 3' }}>
        {previewUrl ? (
          <img
            src={previewUrl}
            alt=""
            draggable={false}
            className="w-full h-full object-cover object-top"
            style={{ opacity: 0.7 }}
          />
        ) : (
          <div
            className="w-full h-full flex items-center justify-center text-3xl"
            style={{ background: `${GOLD} 0.04)`, color: `${GOLD} 0.15)` }}
          >
            &#x2726;
          </div>
        )}
        <div
          className="absolute inset-0"
          style={{ background: 'linear-gradient(to top, #0c0a08 0%, transparent 60%)' }}
        />
        {/* Card count badge */}
        <div
          className="absolute top-2 right-2 text-[0.625cqw] px-1.5 py-0.5 rounded tabular-nums"
          style={{ background: 'rgba(0,0,0,0.6)', color: TEXT_MUTED }}
        >
          {deck.cardCount || deck.cards?.length || 0}
        </div>
      </div>

      {/* Info + buttons */}
      <div className="px-3 pt-1.5 pb-3">
        <div
          className="arena-heading text-[0.75cqw] font-bold truncate mb-2"
          style={{ color: TEXT_PRIMARY }}
        >
          {deck.name || t('Untitled')}
        </div>
        <div className="flex gap-1.5">
          <button
            type="button"
            className="flex-1 py-1.5 text-[0.625cqw] font-semibold cursor-pointer transition-all rounded-md"
            style={{
              ...BEVELED_BTN,
              color: TEXT_BODY,
              textAlign: 'center',
            }}
            data-sound={UI.CONFIRM}
            onClick={() => onSpawn(deck.id, 1)}
          >
            {t('Player 1')}
          </button>
          <button
            type="button"
            className="flex-1 py-1.5 text-[0.625cqw] font-semibold cursor-pointer transition-all rounded-md"
            style={{
              ...BEVELED_BTN,
              color: TEXT_BODY,
              textAlign: 'center',
            }}
            data-sound={UI.CONFIRM}
            onClick={() => onSpawn(deck.id, 2)}
          >
            {t('Player 2')}
          </button>
        </div>
      </div>
    </div>
  );
}
